import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ProfileDetails } from './components/ProfileDetails';
import { OpeningHours } from './components/OpeningHours';
import Icon, { IconName } from '../../components/Icon';
import UserBadge, { UserBadgeStyle } from './components/UserBadge';
import { getDistanceBetweenPlebs, Pleb } from '../../models/Pleb';
import { plebsRepository } from '../../repositories/PlebsRepository';
import { useAuth } from '../../hooks/useAuth';
import Loader from './components/Loader';
import { ConnectionButton } from './components/ConnectionButton';
import { useRouter } from '../../hooks/useRouter';
import { useTheme } from '../../hooks/useTheme';
import { useConfirm } from '../../hooks/useConfirm';
import Slider from 'react-slick';
import { Button } from '../../components/Button';
import { ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import { ButtonSendTo } from '../../components/ButtonSendTo';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { eventsRepository } from '../../repositories/EventsRepository'; 
import { Event, FormatDate } from '../../models/Event';
import Clickable from '../../components/Clickable';
import branch from 'branch-sdk';

interface ProfilePageUserProps {}

export const ProfilePageUser: React.FC<ProfilePageUserProps> = () => {
  const { userId } = useParams<{ userId: string }>();
  const {pleb} = useAuth();
  const [user, setUser] = useState<Pleb | null>(null);
  const [distance, setDistance] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  const {navigateFromFullChannelId, navigateToEvent, navigateToCall} = useRouter();
  const {isDarkMode} = useTheme();
  const {openConnectDialog} = useConfirm();
  const {isMobile} = useTheme();
  const [branchLink, setBranchLink] = useState<string | null>(null);

  useEffect(() => {
    if(userId === pleb?.id) {
      if(pleb)
        setUser(new Pleb(pleb));
      else
        setUser(null);
      setDistance(null);
    }
    else
    {
      setUser(null);
      setDistance(null);

      plebsRepository.getUserDetails(userId ?? '', pleb?.id ?? '')
        .then((user) => {
          setUser(user);
          if(user && pleb) {
            setDistance(getDistanceBetweenPlebs(user, pleb));
          }
        })
        .catch((error) => {
          console.error('Error fetching user:', error);
        });
    }

    eventsRepository.getEventsCreatedByUser(userId ?? '', pleb?.id ?? '', 0, 10)
      .then((events) => {
        setEvents(events.data);
      })
      .catch((error) => {
        console.error('Error fetching events:', error);
      });
  }, [userId, pleb]);

  const getBranchLink = async () : Promise<string|null> => {
    if(user && branchLink === null) {
      const linkData = {
        alias: user.slug,
        data: {
          $og_title: user.name,
          $og_description: '⚡ Stack Friends Who Stack Sats ⚡',
          $canonical_identifier: `user/${user.id}`,
          id: user.id,
          kind: "user",
          $creation_timestamp: Date.now(),
          $always_deeplink: true,
          $canonical_url: `orangepillapp://opa/user/${user.id}`,
        }
      }

      try {
        return new Promise((resolve, reject) => {
          branch.link(linkData, (err, link) => {
            if(err) {
              resolve(`https://orangepill.app.link/${user.slug}`);
            } else {
              setBranchLink(link);
              resolve(link);
            }
          });
        });
      } catch (error) {
        console.error('Error generating Branch link:', error);
        return null;
      }
    }

    return branchLink;
  }

  const handleClickConnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if(user)
      openConnectDialog(user.toJson());
  }

  const sliderSettings = {
    dots: true,
    infinite: false
  };

  return (
    <LayoutWithTopBar
      /*topBarEndPart={
        (userId !== pleb?.id) && (
          <div className="flex flex-row items-center gap-1">
              <Clickable 
                  className="cursor-pointer flex items-center justify-center rounded-xl h-[50px]" 
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    if(userId)
                        navigateToCall();
                }}
                style={{ width: isMobile ? '35px' : '50px'}}
            >
                <span className="material-icons text-brand-orange text-[30px]">call</span>
            </Clickable>
          </div>
        )
      }*/
      onClickPrevArrow={() => navigate(-1)}
      /*topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{user?.name}</h1>
          {user?.badge && <div className="ml-2"><UserBadge badge={user.badge} /></div>}
        </div>
      }*/
    >
      {
        user ? (
          <div className="max-w-[440px] w-full mx-auto py-6">
            <div className="rounded-lg bg-brand-bg-4 overflow-hidden" style={{ maxWidth: '100vw' }}>
              <div className="relative">
                <div>
                  <Slider {...sliderSettings}>
                    {
                      user.pictures.map((picture, index) => (
                        <div key={index}>
                          <img src={picture} alt={user.name} className="w-full aspect-square object-cover" />
                        </div>
                      ))
                    }
                  </Slider>
                </div>
                {user.badge && (
                  <div className="absolute top-4 right-4">
                    <UserBadge badge={user.badge} style={UserBadgeStyle.big} />
                  </div>
                )}
                <div className="absolute bottom-4 left-4">
                  <Button 
                    variant={ButtonVariant.primary} 
                    size={ButtonSize.xsmall} 
                    className="gap-1 rounded-xl"
                  >
                    <CopyToClipboard toCopy={() => getBranchLink()} copiedText="Copied">
                      <div className="flex items-center gap-2 px-2">
                        <span className="material-symbols-outlined text-[14px]">ios_share</span>
                        <span>Share</span>
                      </div>
                    </CopyToClipboard>
                  </Button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">{user.name}</h2>
                    <p className="">{user.formattedSlug}</p>
                  </div>
                  <div className="flex justify-end items-center gap-2">
                    <ButtonSendTo 
                        pleb={user}
                        width={30}
                    />
                    {user.twitterSlug && (
                      <a 
                        href={`https://x.com/${user.twitterSlug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon 
                          name={IconName.TWITTER} 
                          className="w-6 h-6" 
                          color={isDarkMode ? '#FFFFFF' : '#000000'}
                        />
                      </a>
                    )}
                    {
                        user.connection &&
                          <ConnectionButton 
                            connection={user.connection}
                            onConnect={handleClickConnect}
                            onChat={(connectionId) => {
                              navigateFromFullChannelId(connectionId);
                            }}
                          />
                    }
                    {
                      userId === pleb?.id &&
                        <Button
                          variant={ButtonVariant.inverted}
                          size={ButtonSize.small}
                          className="gap-1 rounded-xl"
                          onClick={() => {
                            if(isMobile)
                              navigate('/home/edit-profile');
                            else
                              navigate('/home/edit-profile/user/' + userId);
                          }}
                        >
                          Edit profile
                        </Button>
                    }
                  </div>
                </div>

                <hr className="my-3" />

                {
                  distance && (
                    <div className="mb-4 italic">
                      {distance} away
                    </div>
                  )
                }

                {/* Bio Section */}
                {user.bio && (
                  <div className="prose max-w-none mb-6">
                    {user.bio}
                  </div>
                )}

                {/* Profile Details */}
                <ProfileDetails user={user} />

                {/* Opening Hours */}
                {user.openingTimes && Object.keys(user.openingTimes).length > 0 && (
                  <OpeningHours openingTimes={user.openingTimes} />
                )}
              </div>

              {
                events.length > 0 && <div className="px-6 pb-6">
                  <h2 className="text-xl font-bold mb-2">Events ({events.length})</h2>
                  <div className="flex flex-col gap-3">
                    {
                      events.map((event) => (
                        <Clickable 
                          key={event.id} 
                          className="flex flex-row gap-2 rounded-md" 
                          onClick={() => navigateToEvent(event.id)}
                        >
                          <img src={event.image} alt={event.name} className="w-[80px] h-[80px] aspect-square object-cover rounded-lg shrink-0" />
                          <div>
                            <span className="text-[10px] font-semibold shrink-1 rounded-sm px-1 bg-white text-black">ORGANIZER</span>
                            <h3 className='font-semibold'>{event.name}</h3>
                            <div className="text-sm">
                              {event.getDateFormatted(FormatDate.dateHour)}
                            </div>
                          </div>
                        </Clickable>
                      ))
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        ) : (
          <Loader />
        )
      }
    </LayoutWithTopBar>
  );
};

export default ProfilePageUser;